import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share, Plus, X, Smartphone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const DISMISS_KEY = 'bestbuddy_install_banner_dismissed_at';
const DISMISS_COOLDOWN_DAYS = 7;

export default function InstallPWABanner() {
    const { language } = useLanguage();
    const [show, setShow] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);
    const [installEvent, setInstallEvent] = useState(null);
    const [platform, setPlatform] = useState('other'); // ios | android | desktop | other

    useEffect(() => {
        // Already installed as PWA?
        const isStandalone =
            window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;
        if (isStandalone) return;

        // Dismissed recently?
        const dismissedAt = localStorage.getItem(DISMISS_KEY);
        if (dismissedAt) {
            const days = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
            if (days < DISMISS_COOLDOWN_DAYS) return;
        }

        // Detect platform
        const ua = window.navigator.userAgent;
        const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isAndroid = /Android/.test(ua);
        if (isIOS) setPlatform('ios');
        else if (isAndroid) setPlatform('android');
        else if (window.innerWidth > 768) setPlatform('desktop');

        // Android/Chrome beforeinstallprompt
        const handleBeforeInstall = (e) => {
            e.preventDefault();
            setInstallEvent(e);
            setShow(true);
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstall);

        // iOS: show banner after a short delay (no native prompt available)
        if (isIOS) {
            const tm = setTimeout(() => setShow(true), 2500);
            return () => {
                clearTimeout(tm);
                window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
            };
        }

        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    }, []);

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setShow(false);
        setShowInstructions(false);
    };

    const handleInstallClick = async () => {
        if (installEvent) {
            installEvent.prompt();
            const choice = await installEvent.userChoice;
            if (choice.outcome === 'accepted') {
                dismiss();
            } else {
                dismiss();
            }
        } else {
            setShowInstructions(true);
        }
    };

    if (!show) return null;

    const t = (de, en) => (language === 'de' ? de : en);

    return (
        <AnimatePresence>
            {show && (
                <>
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                        className="lg:hidden fixed left-3 right-3 z-50 rounded-2xl shadow-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card overflow-hidden"
                        style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + 5rem)` }}
                        data-testid="pwa-install-banner"
                    >
                        <div className="p-3 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                                <Smartphone className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm leading-tight">
                                    {t('BESTBUDDY zum Home-Bildschirm', 'Add BESTBUDDY to Home Screen')}
                                </p>
                                <p className="text-[11px] text-muted-foreground truncate">
                                    {t('Schneller Zugriff + Push-Benachrichtigungen', 'Quick access + push notifications')}
                                </p>
                            </div>
                            <button
                                onClick={handleInstallClick}
                                className="px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0 hover:bg-primary/90"
                                data-testid="pwa-install-btn"
                            >
                                {t('Installieren', 'Install')}
                            </button>
                            <button
                                onClick={dismiss}
                                className="p-1 text-muted-foreground hover:text-foreground shrink-0"
                                data-testid="pwa-dismiss-btn"
                                aria-label="dismiss"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.div>

                    {/* iOS install instructions modal */}
                    {showInstructions && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[60] bg-foreground/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                            onClick={() => setShowInstructions(false)}
                        >
                            <motion.div
                                initial={{ y: 80, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 80, opacity: 0 }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full max-w-md bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
                                data-testid="pwa-instructions-modal"
                            >
                                <div className="p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="font-display text-lg font-bold flex items-center gap-2">
                                            <Smartphone className="w-5 h-5 text-primary" />
                                            {t('App installieren', 'Install app')}
                                        </h3>
                                        <button onClick={() => setShowInstructions(false)} className="p-1 text-muted-foreground" aria-label="close">
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>

                                    {platform === 'ios' ? (
                                        <>
                                            <p className="text-sm text-muted-foreground mb-4">
                                                {t('In 3 Schritten zum Home-Bildschirm:', 'Three steps to your home screen:')}
                                            </p>
                                            <ol className="space-y-3">
                                                <li className="flex items-start gap-3">
                                                    <span className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold shrink-0">1</span>
                                                    <div className="text-sm">
                                                        {t('Tippe auf das', 'Tap the')}{' '}
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-muted text-[12px] font-medium">
                                                            <Share className="w-3.5 h-3.5" /> {t('Teilen', 'Share')}
                                                        </span>
                                                        {' '}{t('Symbol unten in Safari.', 'icon in Safari (bottom bar).')}
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <span className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold shrink-0">2</span>
                                                    <div className="text-sm">
                                                        {t('Wähle', 'Choose')}{' '}
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-muted text-[12px] font-medium">
                                                            <Plus className="w-3.5 h-3.5" /> {t('Zum Home-Bildschirm', 'Add to Home Screen')}
                                                        </span>
                                                        .
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <span className="w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold shrink-0">3</span>
                                                    <div className="text-sm">
                                                        {t('Tippe oben rechts auf', 'Tap')}{' '}
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-muted text-[12px] font-medium">
                                                            {t('Hinzufügen', 'Add')}
                                                        </span>
                                                        {' '}{t('— fertig! Nanouk wartet auf dich. 🐺', '— done! Nanouk is waiting. 🐺')}
                                                    </div>
                                                </li>
                                            </ol>
                                            <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
                                                {t('Tipp: Ab iOS 16.4 funktionieren auch Push-Benachrichtigungen für PWAs.', 'Tip: From iOS 16.4 push notifications also work for PWAs.')}
                                            </p>
                                        </>
                                    ) : platform === 'android' ? (
                                        <>
                                            <p className="text-sm text-muted-foreground mb-4">
                                                {t('Chrome zeigt normalerweise einen Installations-Hinweis. Falls nicht:', 'Chrome usually shows an install hint. Otherwise:')}
                                            </p>
                                            <ol className="space-y-3 text-sm">
                                                <li><strong>1.</strong> {t('Öffne das Browser-Menü (3 Punkte oben rechts).', 'Open browser menu (3 dots top right).')}</li>
                                                <li><strong>2.</strong> {t('Wähle „App installieren" oder „Zum Startbildschirm hinzufügen".', 'Pick "Install app" or "Add to Home Screen".')}</li>
                                                <li><strong>3.</strong> {t('Bestätige mit „Installieren". Fertig!', 'Confirm "Install". Done!')}</li>
                                            </ol>
                                        </>
                                    ) : (
                                        <p className="text-sm">
                                            {t('Auf dem Desktop kannst du BESTBUDDY über das Installations-Symbol in der Browser-Adresszeile installieren.', 'On desktop, install BESTBUDDY via the install icon in the browser address bar.')}
                                        </p>
                                    )}

                                    <button
                                        onClick={dismiss}
                                        className="mt-5 w-full py-2.5 rounded-xl bg-muted text-foreground text-sm font-medium hover:bg-muted/80"
                                        data-testid="pwa-instructions-close-btn"
                                    >
                                        {t('Verstanden', 'Got it')}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </>
            )}
        </AnimatePresence>
    );
}
